/**
 * @fileoverview Shared run-flow commit step (Commit): pre-commit summary,
 *               commit progress, retry, post-commit row explorer, and the
 *               save-as-variant / save-as-new-template cards.
 * @module import-editor/runFlow/steps/RunCommitStep
 *
 * Responsibilities:
 * - Render the commit step: ready-to-commit summary, full-sync warning +
 *   optional type-to-confirm gate, commit progress indicator, retry on
 *   failure, post-commit row explorer (capability-gated), and the
 *   save-as-variant / save-as-new-template preservation cards.
 *
 * Dependencies:
 * - `../useImportRunFlow` — the shared state machine (via the `flow` prop).
 * - `../../components/ImportProgressIndicator`, `SaveAsVariantCard`,
 *   `SaveAsNewTemplateCard` — shared run-flow UI.
 * - `../dryRunSummary/DryRunSummaryStep` — post-commit category explorer.
 * - `../DeleteConfirmModal` — full-sync confirmation gate.
 *
 * Architecture:
 * - Presentation-only; commit/cancel/save transitions are hook handlers and
 *   all I/O goes through the host's services adapter.
 *
 * Notes:
 * - Extracted from tenant-web's ImportWizardContainer (CommitStepContent +
 *   DeleteConfirmModal wiring) and system-web's SystemImportWizardContainer
 *   commit case during the Phase 2A run-flow migration (G-002).
 */

import { useMemo, type ReactNode } from 'react';
import type { ImportTemplateListItem } from '@firesquirrel/local-db';
import { ImportProgressIndicator } from '../../components/ImportProgressIndicator';
import SaveAsVariantCard, { type VariantOption } from '../../components/SaveAsVariantCard';
import SaveAsNewTemplateCard from '../../components/SaveAsNewTemplateCard';
import { DryRunSummaryStep } from '../dryRunSummary/DryRunSummaryStep';
import type { DryRunSummaryView } from '../dryRunSummary/types';
import { DeleteConfirmModal } from '../DeleteConfirmModal';
import type { ImportRunFlow } from '../useImportRunFlow';
import { getProgressSummaryTotals } from '../useImportRunFlow';

/**
 * @contract RunCommitStepProps
 *
 * Purpose:
 * - Wire the Commit step to the run-flow state machine plus host options.
 *
 * Fields:
 * - flow: the `useImportRunFlow` result.
 * - requireFullSyncDeleteConfirm: gate full-sync commits behind the
 *   type-to-confirm modal (tenant-web).
 * - defaultTemplateVisibility: visibility stamped on templates created from
 *   the post-commit cards (system-web passes 'system-only').
 * - commitHistoryAccessory: host-rendered history link shown after a
 *   successful commit (tenant-web passes a router Link).
 * - onComplete: host callback for the "View Import History" button
 *   (system-web).
 */
export interface RunCommitStepProps {
  flow: ImportRunFlow;
  requireFullSyncDeleteConfirm?: boolean;
  defaultTemplateVisibility?: string;
  commitHistoryAccessory?: ReactNode;
  onComplete?: () => void;
}

/**
 * @component RunCommitStep
 *
 * Purpose:
 * - Render the run flow's Commit step.
 *
 * Props:
 * - RunCommitStepProps.
 *
 * Behavior:
 * - Commit is disabled until a dry run has completed; full-sync behavior
 *   shows a warning and (optionally) requires the delete-confirm modal.
 * - After completion the step offers variant/template preservation and a
 *   path to import history.
 *
 * Data Source:
 * - Wizard state + live progress from the hook; row details via the
 *   adapter's listImportRunRows when available.
 *
 * Constraints:
 * - No direct API calls; no Dexie access.
 *
 * Performance:
 * - Renders the frozen progress indicator + lazy row explorer after commit.
 */
export default function RunCommitStep({
  flow,
  requireFullSyncDeleteConfirm = false,
  defaultTemplateVisibility,
  commitHistoryAccessory,
  onComplete,
}: RunCommitStepProps) {
  const { state, services, templates } = flow;
  const s = state.dryRunSummary;
  const liveProgress = flow.liveProgress;
  const isDelete = state.importBehavior === 'full-sync';
  const isCommitting = state.commitStatus === 'committing';
  const isDone = state.commitStatus === 'completed';
  const isFailed = state.commitStatus === 'failed';

  // Shell detection + variants list for the save-as-variant card.
  const t = state.selectedTemplate;
  const isShellSource = !!t && !t.parentTemplateId && (t.fieldMappingCount ?? 0) === 0;
  const shellVariants: VariantOption[] = useMemo(
    () => isShellSource && t
      ? templates
          .filter((x: ImportTemplateListItem) => x.parentTemplateId === t._id)
          .map(x => ({
            _id: x._id,
            name: x.name,
            fieldMappingCount: x.fieldMappingCount,
            lastRunAt: x.lastRunAt,
          }))
      : [],
    [isShellSource, t, templates],
  );

  const commitButton = (
    <button
      type="button"
      onClick={isDelete && requireFullSyncDeleteConfirm ? () => flow.setShowDeleteModal(true) : flow.handleCommit}
      disabled={isCommitting || !state.dryRunId || !s}
      className={`px-4 py-2 rounded-md text-sm font-medium text-white disabled:opacity-60 disabled:cursor-not-allowed ${
        isDelete && requireFullSyncDeleteConfirm
          ? 'bg-red-600 hover:bg-red-700'
          : 'bg-emerald-600 hover:bg-emerald-700'
      }`}
    >
      {isCommitting ? 'Committing…' : isDelete && requireFullSyncDeleteConfirm ? 'Delete Records…' : 'Commit Import'}
    </button>
  );

  // In-flight / terminal commit view: frozen progress + post-commit cards.
  if (isCommitting || isDone || isFailed) {
    return (
      <div className="space-y-4">
        <ImportProgressIndicator
          phase={isDone ? 'done' : isFailed ? 'failed' : liveProgress.phase}
          summary={liveProgress.summary}
          startedAt={liveProgress.startedAt}
          frozen={!isCommitting}
          errorMessage={state.commitError}
          stage={liveProgress.stage}
          stageDetail={liveProgress.stageDetail}
        />
        {isFailed && state.dryRunId && (
          <button
            type="button"
            onClick={flow.handleCommit}
            className="px-4 py-1.5 text-xs font-medium text-white bg-emerald-600 hover:bg-emerald-700 rounded-md"
          >
            Retry / Resume Commit
          </button>
        )}
        {/* Post-commit mirror of the dry-run explorer — same run id, lazy
            row fetches, past-tense labels. */}
        {isDone && liveProgress.summary && state.dryRunId && services.listImportRunRows && (
          <DryRunSummaryStep
            summary={liveProgress.summary as unknown as DryRunSummaryView}
            runId={state.dryRunId}
            mode="commit"
            fetchRows={(params) => services.listImportRunRows!(params.runId, {
              category: params.category,
              page: params.page,
              pageSize: params.pageSize,
              q: params.q,
              sort: params.sort,
              signal: params.signal,
            })}
            onExportCsv={services.downloadImportRunRowsCsv
              ? ({ category, q }) => {
                  if (!state.dryRunId) return;
                  services.downloadImportRunRowsCsv!(state.dryRunId, { category, q }).catch((err) => {
                    console.error('[ImportRunFlow] CSV export failed:', err);
                  });
                }
              : undefined
            }
          />
        )}
        {isDone && isShellSource && !flow.saveVariantDismissed && (
          <SaveAsVariantCard
            shellName={t?.name}
            variants={shellVariants}
            saving={flow.saveVariantBusy}
            error={flow.saveVariantError}
            onSaveAsNew={(name) => flow.handleSaveAsNewVariant(name, defaultTemplateVisibility)}
            onUpdateVariant={flow.handleUpdateExistingVariant}
            onDismiss={() => flow.setSaveVariantDismissed(true)}
          />
        )}
        {/* Gold-star flow: the user ran an import WITHOUT selecting a
            template — offer to save the inline mapping work as a fresh
            top-level template. Mutually exclusive with the variant card. */}
        {isDone && !state.selectedTemplate && !flow.saveAsTemplateDismissed && (
          <SaveAsNewTemplateCard
            defaultName={state.importName}
            defaultDescription={state.importDescription}
            saving={flow.saveAsTemplateBusy}
            error={flow.saveAsTemplateError}
            onSave={(name, description) => flow.handleSaveAsNewTemplate(name, description, defaultTemplateVisibility)}
            onDismiss={() => flow.setSaveAsTemplateDismissed(true)}
          />
        )}
        {isDone && state.commitRunId && (
          <div className="flex items-center justify-between gap-3">
            <div className="text-xs text-gray-500">Run ID: <span className="font-mono">{state.commitRunId}</span></div>
            {commitHistoryAccessory}
          </div>
        )}
        {isDone && onComplete && (
          <button type="button" onClick={onComplete}
            className="px-4 py-1.5 text-xs font-medium text-white bg-orange-600 hover:bg-orange-700 rounded-md">
            View Import History
          </button>
        )}
      </div>
    );
  }

  return (
    <>
      {flow.showDeleteModal && s && (
        <DeleteConfirmModal
          count={s.totalRows}
          onConfirm={() => { flow.setShowDeleteModal(false); void flow.handleCommit(); }}
          onCancel={() => flow.setShowDeleteModal(false)}
        />
      )}
      <div className="space-y-4">
        <p className="text-xs text-gray-500">
          You are about to commit this import. This will write all records to the database permanently.
          {s && ` ${s.created.toLocaleString()} records will be created, ${s.updated.toLocaleString()} updated.`}
        </p>
        {s ? (() => {
          // April 2026 taxonomy only — derive the skip/error totals from the
          // nested excluded.* and failedByKind.* sub-buckets.
          const { excludedTotal, failedTotal } = getProgressSummaryTotals(s);
          return (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="text-xs font-semibold text-green-800 mb-2">Ready to commit</div>
            <div className="text-xs text-green-700 space-y-1">
              <div>Creates: <strong>{s.created.toLocaleString()}</strong></div>
              <div>Updates: <strong>{s.updated.toLocaleString()}</strong></div>
              <div>Skips: <strong>{excludedTotal.toLocaleString()}</strong></div>
              {failedTotal > 0 && (
                <div className="text-amber-700">Errors: <strong>{failedTotal.toLocaleString()}</strong> rows will be skipped.</div>
              )}
            </div>
          </div>
          );
        })() : (
          <div className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            Run a dry-run first before committing.
          </div>
        )}
        {state.importBehavior === 'full-sync' && (
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-sm p-3">
            ⚠ Full Sync — records not present in this file will be soft-deleted.
          </div>
        )}
        {commitButton}
      </div>
    </>
  );
}
